"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ALL_HR_PERMISSIONS = exports.HrPermissions = void 0;
/**
 * HR & Workforce permission catalog — Phase 1 (docs/HR_PHASE1_ARCHITECTURE.md §9).
 *
 * ONE FAMILY, `hr:`, granular per the master brief's Module AL. Kept lean on
 * purpose: the JWT embeds EVERY permission a user holds and this platform has
 * hit two header ceilings weeks apart (Node 16KB → 431; nginx buffers → 400,
 * silent). There must never be per-department or per-employee grants here.
 *
 * SCOPE IS A SUFFIX, ENFORCED SERVER-SIDE. `view` = self only; `view-team` =
 * the caller's reporting subtree; `view-all` = the tenant (still branch/dept
 * fenced in the repository). Deny by default — the wider grant must be PRESENT
 * to widen; its absence yields the narrower scope, never the wider.
 *
 * REGISTRATION IS TWO EDITS. Adding this catalog means `seedHrCatalog()` in
 * `permission.service.ts` AND `'seedHrCatalog'` in
 * `scripts/deploy-seed-all-catalogs.ts`'s CATALOG_SEEDERS. A family that exists
 * here but is never seeded produces routes that 403 for every user including
 * SuperAdmin — the ADR-085 Phase 0 deploy shipped eleven routes exactly that
 * way, and the only signal was the catalog COUNT in the deploy log.
 *
 * The pre-existing `employee-master:*` / `designation:*` grants under
 * `MasterDataPermissions` are NOT replaced — that master-data surface keeps
 * working unchanged. This family is the OPERATIONAL HR domain.
 */
exports.HrPermissions = {
    // ── attendance (Phase 1A) ──────────────────────────────────────────────
    HR_ATTENDANCE_VIEW: 'hr:attendance:view',
    HR_ATTENDANCE_VIEW_TEAM: 'hr:attendance:view-team',
    HR_ATTENDANCE_VIEW_ALL: 'hr:attendance:view-all',
    /** Enter a manual punch / import events. Never implies correction or approval. */
    HR_ATTENDANCE_RECORD: 'hr:attendance:record',
    /** Machine ingestion path (device / integration service token). Not a UI role. */
    HR_ATTENDANCE_INGEST: 'hr:attendance:ingest',
    /** Recompute a day from its events (HR desk). */
    HR_ATTENDANCE_MANAGE: 'hr:attendance:manage',
    HR_ATTENDANCE_CORRECT: 'hr:attendance:correct',
    HR_ATTENDANCE_APPROVE_CORRECTION: 'hr:attendance:approve-correction',
    HR_OVERTIME_VIEW: 'hr:overtime:view',
    HR_OVERTIME_REQUEST: 'hr:overtime:request',
    HR_OVERTIME_APPROVE: 'hr:overtime:approve',
    // ── leave (Phase 1B — declared now so the family is defined once) ──────
    HR_LEAVE_VIEW: 'hr:leave:view',
    HR_LEAVE_VIEW_TEAM: 'hr:leave:view-team',
    HR_LEAVE_VIEW_ALL: 'hr:leave:view-all',
    HR_LEAVE_REQUEST: 'hr:leave:request',
    HR_LEAVE_APPROVE: 'hr:leave:approve',
    HR_LEAVE_CANCEL_ANY: 'hr:leave:cancel-any',
    HR_LEAVE_CONFIG: 'hr:leave:config',
    // ── roster (Phase 1C) ────────────────────────────────────────────────
    HR_ROSTER_VIEW: 'hr:roster:view',
    HR_ROSTER_VIEW_ALL: 'hr:roster:view-all',
    HR_ROSTER_MANAGE: 'hr:roster:manage',
    HR_ROSTER_PUBLISH: 'hr:roster:publish',
    HR_ROSTER_CONFIG: 'hr:roster:config',
    // ── credentials + documents (Phase 1D) ──────────────────────────────
    HR_CREDENTIAL_VIEW: 'hr:credential:view',
    HR_CREDENTIAL_VIEW_ALL: 'hr:credential:view-all',
    HR_CREDENTIAL_MANAGE: 'hr:credential:manage',
    HR_CREDENTIAL_VERIFY: 'hr:credential:verify',
    HR_DOCUMENT_VIEW: 'hr:document:view',
    HR_DOCUMENT_VIEW_ALL: 'hr:document:view-all',
    HR_DOCUMENT_MANAGE: 'hr:document:manage',
    HR_DOCUMENT_VERIFY: 'hr:document:verify',
    HR_DOCUMENT_DOWNLOAD: 'hr:document:download',
    HR_COMPLIANCE_VIEW: 'hr:compliance:view',
    // ── job requisition (Phase 2A — recruitment) ────────────────────────
    /** The requester's own requisitions. */
    HR_REQUISITION_VIEW: 'hr:requisition:view',
    /** The tenant-wide requisition register. */
    HR_REQUISITION_VIEW_ALL: 'hr:requisition:view-all',
    HR_REQUISITION_CREATE: 'hr:requisition:create',
    HR_REQUISITION_APPROVE: 'hr:requisition:approve',
    /** Mark an approved requisition CLOSED (positions filled) — a distinct act from deciding it. */
    HR_REQUISITION_CLOSE: 'hr:requisition:close',
    // ── job opening + candidate (Phase 2B — recruitment) ────────────────
    HR_JOB_OPENING_VIEW: 'hr:job-opening:view',
    /** Create / close / hold / reopen a job opening. */
    HR_JOB_OPENING_MANAGE: 'hr:job-opening:manage',
    HR_CANDIDATE_VIEW: 'hr:candidate:view',
    /** Register / edit a candidate. */
    HR_CANDIDATE_MANAGE: 'hr:candidate:manage',
    /** Move a candidate's pipeline stage — a distinct act from editing their record. */
    HR_CANDIDATE_ADVANCE: 'hr:candidate:advance',
    HR_CANDIDATE_DOWNLOAD_RESUME: 'hr:candidate:download-resume',
    // ── interview (Phase 2C — recruitment) ──────────────────────────────
    HR_INTERVIEW_VIEW: 'hr:interview:view',
    /** Schedule / cancel an interview round. */
    HR_INTERVIEW_MANAGE: 'hr:interview:manage',
    /** Record feedback/rating/recommendation — a distinct act from scheduling. */
    HR_INTERVIEW_RECORD_FEEDBACK: 'hr:interview:record-feedback',
    // ── offer (Phase 2D — recruitment, closes out Phase 2) ──────────────
    HR_OFFER_VIEW: 'hr:offer:view',
    /** Extend / withdraw an offer. */
    HR_OFFER_MANAGE: 'hr:offer:manage',
    /** Record the candidate's own accept/decline — a distinct act from extending it. */
    HR_OFFER_RESPOND: 'hr:offer:respond',
    // ── onboarding (Phase 3) ────────────────────────────────────────────
    HR_ONBOARDING_VIEW: 'hr:onboarding:view',
    /** Start onboarding, link the created Employee, complete the checklist. */
    HR_ONBOARDING_MANAGE: 'hr:onboarding:manage',
    // ── training & development (Phase 4) ────────────────────────────────
    HR_TRAINING_VIEW: 'hr:training:view',
    /** Catalogue + session CRUD (programs, sessions). */
    HR_TRAINING_MANAGE: 'hr:training:manage',
    /** Enroll an employee into a session — a distinct act from managing the catalogue. */
    HR_TRAINING_ENROLL: 'hr:training:enroll',
    /** Mark attended/completed, record score, upload a certificate. */
    HR_TRAINING_RECORD: 'hr:training:record',
};
exports.ALL_HR_PERMISSIONS = Object.values(exports.HrPermissions);
//# sourceMappingURL=hr.permissions.js.map